'use client';

import type { LeadEventWithStatusDto } from '@up2stay/up2promo-api-client';
import {
  LeadEventLifecycleStatus,
  LeadEventRosterMode,
} from '@up2stay/up2promo-api-client';
import { Card, cn } from '@up2stay/up2promo-ui';
import { Calendar, MapPin, Tag, Users } from 'lucide-react';

type LeadEventDetailSummaryProps = {
  event: LeadEventWithStatusDto;
  coverUrl?: string;
  rangeLabel: string;
  locationLabel: string;
  teamLabel: string;
  statusLabel: string;
  statusMeta?: string | null;
  autoTagLabel: string;
  coverPlaceholder: string;
  metrics: {
    leads: number;
    leadsLabel: string;
    qualifiedPlus: number;
    qualifiedLabel: string;
    perDay: string;
    perDayLabel: string;
  };
};

export function LeadEventDetailSummary({
  event,
  coverUrl,
  rangeLabel,
  locationLabel,
  teamLabel,
  statusLabel,
  statusMeta,
  autoTagLabel,
  coverPlaceholder,
  metrics,
}: LeadEventDetailSummaryProps) {
  const isActive = event.status === LeadEventLifecycleStatus.ACTIVE;
  const isUpcoming = event.status === LeadEventLifecycleStatus.UPCOMING;

  return (
    <Card className="overflow-hidden border-[color:var(--surface-border)] shadow-[var(--shadow-card)]">
      <div className="flex flex-col gap-4 p-4 sm:flex-row sm:items-start sm:gap-5 sm:p-5">
        <div className="flex size-[72px] w-full shrink-0 items-center justify-center overflow-hidden rounded-xl bg-muted text-[11px] text-muted-foreground sm:h-[72px] sm:w-[120px]">
          {coverUrl ? (
            <img src={coverUrl} alt="" className="h-full w-full object-cover" />
          ) : (
            coverPlaceholder
          )}
        </div>

        <div className="flex min-w-0 flex-1 flex-col gap-2">
          <div className="flex flex-wrap items-center gap-2">
            <span
              className={cn(
                'inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-[11.5px] font-semibold',
                isActive
                  ? 'bg-primary/10 text-foreground'
                  : isUpcoming
                    ? 'bg-muted text-muted-foreground'
                    : 'bg-muted text-muted-foreground',
              )}
            >
              <span
                className={cn(
                  'size-1.5 rounded-full',
                  isActive ? 'bg-primary' : 'bg-muted-foreground',
                )}
                aria-hidden
              />
              {statusMeta ? `${statusLabel} · ${statusMeta}` : statusLabel}
            </span>
          </div>

          <div className="flex flex-wrap gap-x-[18px] gap-y-1.5 text-[13px] text-foreground">
            <span className="inline-flex items-center gap-1.5">
              <MapPin className="size-3.5 text-muted-foreground" aria-hidden />
              {locationLabel}
            </span>
            <span className="inline-flex items-center gap-1.5">
              <Calendar
                className="size-3.5 text-muted-foreground"
                aria-hidden
              />
              {rangeLabel}
            </span>
            <span className="inline-flex items-center gap-1.5">
              <Users className="size-3.5 text-muted-foreground" aria-hidden />
              {teamLabel}
            </span>
          </div>

          {event.autoTagEnabled && event.autoTag?.trim() ? (
            <div className="inline-flex items-center gap-1.5 text-[12.5px] text-muted-foreground">
              <Tag className="size-3.5 shrink-0" aria-hidden />
              {autoTagLabel.replace('{tag}', event.autoTag.trim())}
            </div>
          ) : null}

          {event.description?.trim() ? (
            <p className="text-[13px] leading-relaxed text-muted-foreground text-pretty">
              {event.description.trim()}
            </p>
          ) : null}
        </div>

        <div className="flex shrink-0 items-stretch gap-0 sm:pl-2">
          <Metric
            value={String(metrics.leads)}
            label={metrics.leadsLabel}
            bordered={false}
          />
          <Metric
            value={String(metrics.qualifiedPlus)}
            label={metrics.qualifiedLabel}
          />
          <Metric value={metrics.perDay} label={metrics.perDayLabel} />
        </div>
      </div>
      {event.rosterMode === LeadEventRosterMode.SELECTED ? null : null}
    </Card>
  );
}

function Metric({
  value,
  label,
  bordered = true,
}: {
  value: string;
  label: string;
  bordered?: boolean;
}) {
  return (
    <div
      className={cn(
        'flex min-w-[72px] flex-col items-center justify-center px-3 py-1 text-center',
        bordered && 'border-l border-[color:var(--surface-border)]',
      )}
    >
      <div className="font-mono text-[22px] font-bold leading-none tracking-tight">
        {value}
      </div>
      <div className="mt-1 text-[11px] text-muted-foreground">{label}</div>
    </div>
  );
}
